import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ENVIRONMENT } from 'shared';
import { ViacarteBalance, ViacarteTransaction } from './viacarte-admin.service';

/** Eine Karte des Users (vereinfachte, user-sichere Sicht). */
export interface MyCard {
  id: string;
  card_type: number;
  card_type_label: 'physical' | 'virtual' | 'unknown';
  status_network?: number;
  last_four?: string;
  expiry?: string;
  balance?: ViacarteBalance | null;
}

/** ViaCarte-Block der /mycards-Antwort (nur wenn KYC gestartet). */
export interface MyCardsViacarte {
  status: string | null;
  customer_id_present: boolean;
  completed: boolean;
  action_required: boolean;
  failed: boolean;
  workflow_url: string | null;
  cards?: MyCard[];
  account_balance?: ViacarteBalance | null;
  transactions?: ViacarteTransaction[];
}

/**
 * Kartenpartner-Programm (FAC3LESS Platinum, Portal cryptocards.cash). Nur auf
 * fac3less + mnlo geliefert, dort für beide Boards identisch: die Berechtigung ist
 * user-weit, egal ob sie aus einem Kartenkauf oder der mnlo-Lifetime stammt.
 */
export interface MyCardsCardPartner {
  entitled: boolean;
  portal_url: string;
  /** Der Partner kennt den User nur über diese Adresse — damit muss er sich dort anmelden. */
  login_email: string;
  /** Ohne Anspruch: Karten gibt es nur auf fac3less — Cross-Login-Ziel, Seite und Fallback-URL. */
  purchase_site: string;
  purchase_path: string;
  purchase_url: string | null;
  /** true auf fac3less selbst: dann ist der Bestellassistent eine Route dieser App, kein Handoff. */
  purchase_on_site: boolean;
  /** Kompakter Stand beim Kartenpartner (lokal gespiegelt); null, solange kein Partner-Konto verknüpft ist. */
  sync?: CardPartnerSyncStatus | null;
}

export interface CardPartnerSyncStatus {
  account_status: string | null;
  kyc_status: string | null;
  card_active: boolean;
  synced_at: string | null;
}

/**
 * Daten des Kartenpartners (toonpay-Analytics-API), vom Backend in die eigene DB
 * gespiegelt (ToonpaySyncService). Werte im Vokabular des Partners — Labels macht
 * erst das Frontend. Dezimalzahlen kommen als Strings (keine Float-Rundung).
 */
export interface CardPartnerAccount {
  account_status: string | null;
  kyc_status: string | null;
  kyc_current_step: string | null;
  kyc_completed_steps: string[];
  card_activated_at: string | null;
  partner_created_at: string | null;
  synced_at: string | null;
  details_synced_at: string | null;
  /** Nur geliefert, solange der KYC-Status nicht APPROVED ist. */
  kyc_reason?: string | null;
  kyc_reason_category?: string | null;
}

export interface CardPartnerCard {
  id: string;
  last_four: string | null;
  card_status: string | null;
  physical_status: string | null;
  physical_ordered_at: string | null;
  physical_distributed_at: string | null;
  created_at: string | null;
}

export interface CardPartnerDepositAddress {
  network: string;
  token: string;
  address: string;
  memo: string | null;
}

export interface CardPartnerTransaction {
  id: string;
  time: string | null;
  type: string | null;
  status: string | null;
  amount: string | null;
  currency: string | null;
  transaction_amount: string | null;
  transaction_currency: string | null;
  fee: string | null;
  merchant_name: string | null;
  merchant_city: string | null;
  merchant_country: string | null;
  card_last_four: string | null;
}

export interface CardPartnerTransfer {
  id: string;
  time: string | null;
  chain: string | null;
  currency: string | null;
  amount: string | null;
  fee: string | null;
  status: string | null;
  transaction_hash: string | null;
  credited_at?: string | null;
  to_address?: string | null;
}

export interface CardPartnerData {
  /** false: der Partner kennt (noch) kein Konto mit der E-Mail-Adresse dieses Users. */
  linked: boolean;
  account?: CardPartnerAccount;
  cards?: CardPartnerCard[];
  deposit_addresses?: CardPartnerDepositAddress[];
  transactions?: CardPartnerTransaction[];
  transactions_total?: number;
  deposits?: CardPartnerTransfer[];
  deposits_total?: number;
  withdrawals?: CardPartnerTransfer[];
  withdrawals_total?: number;
  refresh_error?: string;
}

export interface CardPartnerPage<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

export type CardPartnerListKind = 'transactions' | 'deposits' | 'withdrawals';

export interface MyCardsSummary {
  app: MyCardsResponse['app'];
  card_partner?: MyCardsCardPartner;
}

export interface MyCardsResponse {
  app: 'fac3less' | 'distribution' | 'globalhealth' | 'mnlo';
  /** false, wenn der VIACARTE_API_KEY in dieser Umgebung nicht gesetzt ist -> "Coming soon". */
  viacarte_configured: boolean;
  /** Fehlt auf Boards ohne Kartenpartner-Programm (globalhealth). */
  card_partner?: MyCardsCardPartner;
  eligible?: boolean;
  kyc_started?: boolean;
  profile_complete?: boolean;
  missing_fields?: string[];
  viacarte?: MyCardsViacarte;
  refresh_error?: string;
}

/**
 * User-facing Kartenbereich (/mycards). In fac3less, mnlo + globalhealth geroutet,
 * generisch (gleiches Backend) → lebt in der credo-Lib.
 */
@Injectable({ providedIn: 'root' })
export class MyCardsService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  get(): Observable<MyCardsResponse> {
    return this.http.get<MyCardsResponse>(`${this.apiUrl}/mycards`);
  }

  /** Nur der Kartenpartner-Block (Dashboard-Widget) — löst keine ViaCarte-Aufrufe aus. */
  summary(): Observable<MyCardsSummary> {
    return this.http.get<MyCardsSummary>(`${this.apiUrl}/mycards/summary`);
  }

  startKyc(occupation: string): Observable<MyCardsResponse> {
    return this.http.post<MyCardsResponse>(`${this.apiUrl}/mycards/start-kyc`, { occupation });
  }

  refresh(): Observable<MyCardsResponse> {
    return this.http.post<MyCardsResponse>(`${this.apiUrl}/mycards/refresh`, {});
  }

  /** Kartenpartner-Daten aus unserer DB (nie live beim Partner). */
  cardPartner(): Observable<CardPartnerData> {
    return this.http.get<CardPartnerData>(`${this.apiUrl}/mycards/card_partner`);
  }

  cardPartnerList<T = CardPartnerTransaction | CardPartnerTransfer>(
    kind: CardPartnerListKind, page: number, perPage: number
  ): Observable<CardPartnerPage<T>> {
    return this.http.get<CardPartnerPage<T>>(`${this.apiUrl}/mycards/card_partner/${kind}`,
      { params: { page, per_page: perPage } });
  }

  /** Zieht die Daten dieses Users jetzt beim Partner nach; 429 = zu oft (max. alle 2 Minuten). */
  refreshCardPartner(): Observable<CardPartnerData> {
    return this.http.post<CardPartnerData>(`${this.apiUrl}/mycards/card_partner/refresh`, {});
  }
}
